export const STREET_SPRINT_BEST_SCORE_KEY = 'jgStreetSprint.bestScore';

export const LANES = [0, 1, 2];
export const MAX_LIVES = 3;
export const GAME_DURATION_MS = 60000;
export const LEVEL_INTERVAL_MS = 12000;
export const TICK_MS = 100;
export const COLLISION_Y = 84;
export const CLEAN_DODGE_VALUE = 35;

export const LOCALOPS_CHAIN = [
  'request',
  'helperMatch',
  'taskComplete',
  'proofReceipt',
  'actionCredit',
  'jgkBoost',
];

export const GOOD_ITEMS = [
  { id: 'request', label: 'Request', icon: 'request', type: 'good', value: 100, chainKey: 'request' },
  { id: 'helperMatch', label: 'Helper Match', icon: 'helper', type: 'good', value: 100, chainKey: 'helperMatch' },
  { id: 'taskComplete', label: 'Task Complete', icon: 'complete', type: 'good', value: 100, chainKey: 'taskComplete' },
  { id: 'proofReceipt', label: 'Proof Receipt', icon: 'proof', type: 'good', value: 100, chainKey: 'proofReceipt' },
  { id: 'actionCredit', label: 'Action Credit', icon: 'credit', type: 'good', value: 150, chainKey: 'actionCredit' },
  { id: 'jgkBoost', label: 'JGK Boost', icon: 'boost', type: 'good', value: 150, chainKey: 'jgkBoost', effect: 'boost' },
  { id: 'localBusiness', label: 'Local Business', icon: 'business', type: 'good', value: 120 },
  { id: 'streetDrop', label: 'Street Drop', icon: 'drop', type: 'good', value: 120 },
];

export const BAD_ITEMS = [
  { id: 'spamFlyer', label: 'Spam Flyer', icon: 'spam', type: 'bad' },
  { id: 'brokenLink', label: 'Broken Link', icon: 'broken', type: 'bad' },
  { id: 'fakeOffer', label: 'Fake Offer', icon: 'fake', type: 'bad' },
  { id: 'missedAuth', label: 'Missed Auth', icon: 'auth', type: 'bad' },
  { id: 'frictionCone', label: 'Friction Cone', icon: 'cone', type: 'bad' },
  { id: 'lowProof', label: 'Low Proof', icon: 'proofWarning', type: 'bad' },
];

export function clampLane(lane) {
  return Math.max(0, Math.min(2, lane));
}

export function calculateLevel(activeElapsedMs) {
  return Math.max(1, Math.floor(Math.max(0, activeElapsedMs) / LEVEL_INTERVAL_MS) + 1);
}

export function getLevelConfig(level) {
  const safeLevel = Math.max(1, Number(level) || 1);
  const ramp = safeLevel - 1;

  return {
    level: safeLevel,
    label:
      safeLevel === 1
        ? 'Clean Start'
        : safeLevel === 2
          ? 'More Friction'
          : safeLevel === 3
            ? 'Faster Streets'
            : safeLevel === 4
              ? 'Tight Turns'
              : 'Overtime Rush',
    speed: Math.min(3.85, 1.55 + ramp * 0.36),
    spawnEveryMs: Math.max(480, 900 - ramp * 90),
    maxItems: safeLevel < 3 ? 2 : 3,
    badWeight: Math.min(0.62, 0.22 + ramp * 0.08),
  };
}

export function createSeededRandom(seed = 1) {
  let state = Math.max(1, Math.floor(seed)) % 2147483647;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
}

function pickFrom(list, random) {
  return list[Math.floor(random() * list.length) % list.length];
}

export function pickItemForLevel(level, random = Math.random) {
  const config = getLevelConfig(level);
  const useBadItem = random() < config.badWeight;
  return useBadItem ? pickFrom(BAD_ITEMS, random) : pickFrom(GOOD_ITEMS, random);
}

export function generateSpawnPattern({ level = 1, random = Math.random, spawnId = 0 } = {}) {
  const config = getLevelConfig(level);
  const itemCount = Math.max(1, Math.min(config.maxItems, 1 + Math.floor(random() * config.maxItems)));
  const lanes = [...LANES];
  const items = [];

  for (let index = 0; index < itemCount; index += 1) {
    const laneIndex = Math.floor(random() * lanes.length) % lanes.length;
    const lane = lanes.splice(laneIndex, 1)[0];
    const definition = pickItemForLevel(level, random);
    items.push({
      ...definition,
      lane,
      y: 0,
      instanceId: `spawn-${spawnId}-${index}-${definition.id}-${lane}`,
    });
  }

  if (items.length === LANES.length && items.every((item) => item.type === 'bad')) {
    items[0] = {
      ...GOOD_ITEMS[spawnId % GOOD_ITEMS.length],
      lane: items[0].lane,
      y: 0,
      instanceId: `spawn-${spawnId}-safe-${items[0].lane}`,
    };
  }

  return items;
}

export function createInitialRunState() {
  return {
    score: 0,
    combo: 0,
    highestCombo: 0,
    lives: MAX_LIVES,
    chainIndex: 0,
    boostUntilMs: 0,
    lastOutcome: '',
  };
}

export function getComboMultiplier(combo) {
  return Math.max(1, Math.min(5, 1 + Math.floor(Math.max(0, combo) / 3)));
}

export function applyCollision(runState, item, { activeElapsedMs = 0 } = {}) {
  if (!item || !item.type) return runState;

  if (item.type === 'bad') {
    return {
      ...runState,
      combo: 0,
      chainIndex: 0,
      lives: Math.max(0, runState.lives - 1),
      lastOutcome: `Hit ${item.label}`,
    };
  }

  const nextCombo = runState.combo + 1;
  const multiplier = getComboMultiplier(nextCombo);
  const boostActive = runState.boostUntilMs > activeElapsedMs;
  let points = (item.value || 100) * multiplier * (boostActive ? 2 : 1);
  let chainIndex = runState.chainIndex;
  let lastOutcome = `+${points} ${item.label}`;

  if (item.chainKey) {
    const expected = LOCALOPS_CHAIN[chainIndex];
    if (item.chainKey === expected) {
      chainIndex += 1;
      if (chainIndex >= LOCALOPS_CHAIN.length) {
        points += 500;
        chainIndex = 0;
        lastOutcome = `Full chain bonus +${points}`;
      }
    } else {
      chainIndex = item.chainKey === LOCALOPS_CHAIN[0] ? 1 : 0;
    }
  }

  return {
    ...runState,
    score: runState.score + points,
    combo: nextCombo,
    highestCombo: Math.max(runState.highestCombo, nextCombo),
    chainIndex,
    boostUntilMs: item.effect === 'boost' ? activeElapsedMs + 5000 : runState.boostUntilMs,
    lastOutcome,
  };
}

export function applyCleanDodge(runState, item, { activeElapsedMs = 0 } = {}) {
  if (!item || item.type !== 'bad') return runState;

  const nextCombo = runState.combo + 1;
  const multiplier = getComboMultiplier(nextCombo);
  const boostActive = runState.boostUntilMs > activeElapsedMs;
  const points = CLEAN_DODGE_VALUE * multiplier * (boostActive ? 2 : 1);

  return {
    ...runState,
    score: runState.score + points,
    combo: nextCombo,
    highestCombo: Math.max(runState.highestCombo, nextCombo),
    lastOutcome: `Clean dodge +${points}`,
  };
}

export function getGrade(score) {
  if (score >= 12000) return 'S';
  if (score >= 9000) return 'A';
  if (score >= 6000) return 'B';
  if (score >= 3000) return 'C';
  return 'D';
}

export function getGradeLabel(grade) {
  return {
    S: 'Excellent run',
    A: 'Strong run',
    B: 'Decent run',
    C: 'Needs cleaner proof',
    D: 'Too much friction',
  }[grade] || 'Too much friction';
}

export function readBestScore(storage = window.localStorage) {
  const value = Number(storage.getItem(STREET_SPRINT_BEST_SCORE_KEY) || 0);
  return Number.isFinite(value) ? value : 0;
}

export function writeBestScore(score, storage = window.localStorage) {
  const current = readBestScore(storage);
  const next = Math.max(current, Number(score) || 0);
  storage.setItem(STREET_SPRINT_BEST_SCORE_KEY, String(next));
  return next;
}
